import { Fragment } from "react";
import { centralLegalUrl } from "@janeway/app-sdk/public-env";

/**
 * Die beiden gesetzlich vorgeschriebenen Schaltflaechen — Kuendigung nach
 * § 312k BGB und Widerruf nach § 356a BGB.
 *
 * WARUM als eigene Komponente und nicht zweimal von Hand: Beide Rechtsleisten
 * der Suite (Login-Bildschirm `AuthOnboarding`, Hub-Shell `HubLauncher`) muessen
 * identisch sein — das stand bisher nur als Bitte im Kommentar und ist genau die
 * Art Parität, die beim naechsten Edit auseinanderlaeuft. Hier ist sie erzwungen.
 *
 * WARUM ueberhaupt hier und nicht nur auf janeway.io: § 312k Abs. 2 verlangt die
 * Schaltflaeche auf der Oberflaeche, ueber die der Vertrag geschlossen werden
 * kann. Kostenpflichtige Abos entstehen in ZWOELF Apps an deren `/billing`
 * (`createCheckout`), nicht nur im Hub. Wer dort ausgeloggt hinkommt, landet per
 * `proxy.ts` → app-sdk-Middleware auf dem Login dieser Komponente — deshalb ist
 * sie die eine ungated Stelle, die alle zwoelf abdeckt.
 *
 * Drei Dinge, die man nicht aendern darf:
 * 1. **Die Beschriftungen.** § 312k Abs. 2 verlangt „Verträge hier kündigen"
 *    (oder eine entsprechend eindeutige Formulierung) und NICHTS anderes; § 356a
 *    entsprechend „Vertrag widerrufen". Kein Zusatz, kein Icon-Text, keine
 *    Verkuerzung auf „Kündigen".
 * 2. **Die Bedingung — es gibt keine.** Diese Links haengen an keinem Login, an
 *    keinem Abo-Zustand und an keinem Stripe-Zustand. Der Portal-Knopf in
 *    `BillingPage` ist Komfort, nicht die Erfuellung der Norm.
 * 3. **Die Lesbarkeit.** „gut lesbar" ist Tatbestandsmerkmal — erfuellt ist es
 *    dadurch, dass diese Links mindestens so gross und so kontrastreich sind wie
 *    jeder andere Eintrag der Leiste. Verboten bleibt der Weg nach unten:
 *    kleiner setzen, weiter daempfen als die Nachbarn oder wegklappen nimmt
 *    § 312k zurueck. Der Kuendigungslink setzt deshalb keine eigene Farbe — er
 *    erbt die der Leiste und ist per Konstruktion nie schlechter lesbar.
 * 4. **Die Hervorhebung des WIDERRUFS.** Sie ist Pflicht, nicht Geschmack:
 *    § 356a Abs. 1 Satz 3 BGB verlangt die Widerrufsfunktion „staendig
 *    verfuegbar, HERVORGEHOBEN PLATZIERT und fuer den Verbraucher leicht
 *    zugaenglich" (Fassung ab 19.06.2026, BGBl. 2026 I Nr. 28; Wortlaut an drei
 *    Quellen geprueft). § 312k Abs. 2 Satz 4 sagt fuer die
 *    Kuendigungsschaltflaeche nur „unmittelbar und leicht zugaenglich" —
 *    „hervorgehoben platziert" steht ALLEIN in § 356a. An diesen zwei Woertern
 *    trennen sich die beiden Vorschriften, und deshalb traegt hier genau EIN
 *    Link den Rahmen. Beide gleich abzusetzen waere nicht falsch, macht das
 *    Absetzen aber wieder unsichtbar.
 *
 *    **Hier stand bis 0.368.0 das Gegenteil.** Bis 0.196.0 waren BEIDE Links auf
 *    `font-medium text-foreground` gesetzt; entfernt wurden sie mit der
 *    Begruendung „das verlangt keine Norm". Fuer § 312k stimmt das bis heute —
 *    fuer § 356a nicht, den gab es in dieser Fassung damals noch nicht. Wer
 *    diesen Satz stehen laesst, dreht die Aenderung beim naechsten Mal zurueck.
 *    Waechter: `src/__tests__/kuendigungWiderrufLinks.test.tsx` und
 *    `src/__tests__/pflichtlinks-kontrast.test.ts` (der rechnet den Kontrast
 *    ueber alle Themes, statt Klassennamen zu vergleichen).
 */

const LINK_CLS = "transition-colors hover:text-foreground";
const SEP_CLS = "text-muted-foreground/30";

/**
 * Der Aufschlag fuer die hervorgehobene Schaltflaeche — Rahmen plus die
 * staerkste Textfarbe, dieselbe Form wie im Fuss von `janeway.io`
 * (`janeway-website/src/components/Footer.tsx`), nur in Leistengroesse.
 *
 * **Als Konstante, weil `AbrechnungTab` dieselbe Hervorhebung braucht** — der
 * Reiter bildet `PFLICHTWEGE` selbst ab, und zwei handgeschriebene Fassungen
 * derselben Rechtspflicht laufen beim naechsten Edit auseinander. Die Farbe
 * steht hier und nicht im Kontrast-Waechter: der liest diese Zeile.
 */
export const PFLICHTWEG_HERVOR_CLS =
  "rounded-md border border-border px-2 py-0.5 font-medium text-foreground";

/**
 * Beschriftung und Ziel der beiden Pflichtwege — die EINE Wahrheit dazu.
 *
 * WARUM als Konstante und nicht dreimal im JSX: seit die Abrechnung auch ein
 * System-Reiter der Einstellungen ist (`AbrechnungTab`, § 312k Abs. 2 Satz 3
 * „unmittelbar und leicht zugaenglich"), gibt es eine ZWEITE Darstellungsform
 * derselben zwei Wege — hier als Leisteneintrag, dort als Eintrag im Reiter.
 * Zwei Formen mit abgeschriebenem Wortlaut waeren genau die Paritaet, die beim
 * naechsten Edit auseinanderlaeuft; die Beschriftung ist aber Tatbestands-
 * merkmal (siehe Punkt 1 oben). Wer den Wortlaut aendern will, aendert ihn
 * hier — und faellt dann in beiden Waechtern auf, nicht in einem.
 */
export const PFLICHTWEGE = [
  { slug: "kuendigung", label: "Verträge hier kündigen", hervorgehoben: false },
  // `hervorgehoben` ist die Norm, nicht der Stil: § 356a Abs. 1 Satz 3 BGB
  // verlangt es fuer den Widerruf, § 312k Abs. 2 fuer die Kuendigung nicht
  // (Punkt 4 im Kopf). Das Merkmal steht deshalb HIER, bei Beschriftung und
  // Ziel, und nicht in der Klassenliste einer der beiden Darstellungsformen.
  { slug: "widerruf/erklaeren", label: "Vertrag widerrufen", hervorgehoben: true },
] as const;

/**
 * Die beiden Links plus abschliessendes Trennzeichen — gedacht als ERSTE
 * Eintraege einer bestehenden Rechtsleiste (`<nav>` mit `·`-Trennern).
 */
export function KuendigungWiderrufLinks() {
  return (
    <>
      {PFLICHTWEGE.map((weg) => (
        <Fragment key={weg.slug}>
          <a
            href={centralLegalUrl(weg.slug)}
            className={weg.hervorgehoben ? `${LINK_CLS} ${PFLICHTWEG_HERVOR_CLS}` : LINK_CLS}
          >
            {weg.label}
          </a>
          <span aria-hidden="true" className={SEP_CLS}>·</span>
        </Fragment>
      ))}
    </>
  );
}
